import { api } from "@clarent/backend/convex/_generated/api";
import type { Id } from "@clarent/backend/convex/_generated/dataModel";
import {
  computeSchedule,
  machiningWindows,
  planItems,
  startOfWeek,
  stepHours,
  weeklyLoad,
  type CommittedJob,
  type JobStatus,
  type JobStep,
  type MachineHours,
  type ScheduleResult,
} from "@clarent/scheduling";
import { closestCenter, DndContext, KeyboardSensor, PointerSensor, useSensor, useSensors, type DragEndEvent } from "@dnd-kit/core";
import { arrayMove, SortableContext, sortableKeyboardCoordinates, useSortable, verticalListSortingStrategy } from "@dnd-kit/sortable";
import { CSS } from "@dnd-kit/utilities";
import { useMutation, useQuery } from "convex/react";
import { useCallback, useEffect, useMemo, useRef, useState, type FormEvent, type KeyboardEvent, type ReactNode } from "react";
import { errorMessage } from "../lib/convexError";
import { formatDateLong, formatHours, plural, weekCommencing } from "../lib/format";
import { engineSettings, useMachines, type ShopMachines } from "../lib/machines";
import { formatRange, planItemTitle, workingDaysFromToday } from "../lib/plan";
import { PROCESS_META, processKind } from "../lib/process";
import { STATUS_META } from "../lib/risk";
import { useToday } from "../lib/today";
import { CalendarIcon, ChevronDownIcon, CloseIcon, GripIcon, TrashIcon, WarningIcon } from "./icons";
import { PlanStrip } from "./PlanStrip";
import { RiskBadge } from "./RiskBadge";

type StepRow = JobStep & { key: number };

interface JobFormValues {
  customerName: string;
  reference: string;
  part: string;
  quantity: string;
  notes: string;
  dueDate: string;
  status: JobStatus;
  steps: JobStep[];
}

const POST_PROCESS_SUGGESTIONS = ["Painting", "Anodising", "Powder coating", "Heat treatment", "Plating", "Engraving", "Grinding (subcontract)", "Inspection"];
const IS_MAC = typeof navigator !== "undefined" && /Mac|iPhone|iPad/.test(navigator.platform);

let keyCounter = 1;
const nextKey = () => keyCounter++;

/**
 * New job and edit job, as the design system v2.1 modal: 1140px, 48px from the top, form on the
 * left and the live result panel on the right. Loads the job first when editing.
 */
export function JobModal({ jobId, onClose, onSaved }: { jobId?: Id<"jobs">; onClose: () => void; onSaved: (jobId: Id<"jobs">) => void }) {
  const job = useQuery(api.jobs.get, jobId ? { jobId } : "skip");
  const restore = useMutation(api.jobs.restore);

  useEffect(() => {
    const onKey = (e: globalThis.KeyboardEvent) => e.key === "Escape" && onClose();
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [onClose]);

  let body: ReactNode;
  if (jobId && job === undefined) body = <p className="p-7 text-sm text-muted">Loading job…</p>;
  else if (job && job.archivedAt !== undefined) {
    body = (
      <div className="flex flex-wrap items-center gap-4 p-7">
        <div className="flex-1">
          <h3 className="app-title">This job is archived</h3>
          <p className="mt-1 text-sm text-muted">It isn't on the board or calendar and won't raise alerts. Restore it to change it.</p>
        </div>
        <button type="button" className="btn-primary btn-md" onClick={() => restore({ jobId: job._id })}>
          Restore job
        </button>
      </div>
    );
  } else {
    body = (
      <JobForm
        key={job?._id ?? "new"}
        jobId={jobId}
        initial={
          job
            ? {
                customerName: job.customerName,
                reference: job.reference,
                part: job.part ?? "",
                quantity: job.quantity?.toString() ?? "",
                notes: job.notes ?? "",
                dueDate: job.dueDate,
                status: job.status,
                steps: job.steps,
              }
            : undefined
        }
        onCancel={onClose}
        onSaved={onSaved}
      />
    );
  }

  return (
    <>
      <div className="scrim" onClick={onClose} />
      <div
        role="dialog"
        aria-modal="true"
        aria-labelledby="job-modal-title"
        className="fixed inset-x-3 top-3 bottom-3 z-50 mx-auto flex max-w-[1140px] flex-col overflow-hidden rounded-lg bg-bg shadow-modal lg:inset-x-6 lg:top-12 lg:bottom-auto lg:max-h-[calc(100vh-96px)]"
      >
        <header className="flex items-start justify-between gap-4 px-7 pt-6">
          <div className="flex flex-col gap-1">
            <h2 id="job-modal-title" className="overlay-title">
              {jobId ? (job ? `Edit ${job.reference}` : "Edit job") : "New job"}
            </h2>
            <p className="text-sm leading-[18px] text-muted">Add the due date and the steps. Backplan counts back to the latest start.</p>
          </div>
          <button type="button" className="icon-btn" aria-label="Close" onClick={onClose}>
            <CloseIcon />
          </button>
        </header>
        {body}
      </div>
    </>
  );
}

/**
 * Keyboard model: Enter moves to the next field, Cmd/Ctrl+Enter saves,
 * Alt+M adds a machining step, Alt+P adds a post-process step.
 */
function JobForm({ jobId, initial, onCancel, onSaved }: { jobId?: Id<"jobs">; initial?: JobFormValues; onCancel: () => void; onSaved: (jobId: Id<"jobs">) => void }) {
  const today = useToday();
  const customers = useQuery(api.customers.list);
  const settings = useQuery(api.settings.get);
  const machines = useMachines();
  const committed = useQuery(api.jobs.committedWindows, { today, excludeJobId: jobId });
  const createJob = useMutation(api.jobs.create);
  const updateJob = useMutation(api.jobs.update);

  const [customerName, setCustomerName] = useState(initial?.customerName ?? "");
  const [reference, setReference] = useState(initial?.reference ?? "");
  const [part, setPart] = useState(initial?.part ?? "");
  const [quantity, setQuantity] = useState(initial?.quantity ?? "");
  const [notes, setNotes] = useState(initial?.notes ?? "");
  const [dueDate, setDueDate] = useState(initial?.dueDate ?? "");
  const [status, setStatus] = useState<JobStatus>(initial?.status ?? "scheduled");
  const [steps, setSteps] = useState<StepRow[]>(() => (initial?.steps ?? [{ type: "machining", machines: [{ machineId: "", hours: 0 }] }]).map((s) => ({ ...s, key: nextKey() })));
  const [focusKey, setFocusKey] = useState<number | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [saving, setSaving] = useState(false);
  const formRef = useRef<HTMLFormElement>(null);

  // New machining starts on the first machine; a shop with one machine never has to pick (D49).
  const firstMachineId = machines?.active[0]?._id ?? "";
  const firstMachine = useRef(firstMachineId);
  firstMachine.current = firstMachineId;
  useEffect(() => {
    if (!firstMachineId) return;
    setSteps((prev) => prev.map((s) => (s.type === "machining" && s.machines.some((m) => !m.machineId) ? { ...s, machines: s.machines.map((m) => (m.machineId ? m : { ...m, machineId: firstMachineId })) } : s)));
  }, [firstMachineId]);

  const addStep = useCallback((type: JobStep["type"]) => {
    const key = nextKey();
    setSteps((prev) => [
      ...prev,
      type === "machining"
        ? { key, type: "machining", machines: [{ machineId: firstMachine.current, hours: 0 }] }
        : { key, type: "post_process", name: "", leadDays: 5, transitDaysOut: 1, transitDaysBack: 1 },
    ]);
    setFocusKey(key);
  }, []);

  const updateStep = (key: number, patch: Partial<JobStep>) => setSteps((prev) => prev.map((s) => (s.key === key ? ({ ...s, ...patch } as StepRow) : s)));
  const removeStep = (key: number) => setSteps((prev) => prev.filter((s) => s.key !== key));

  const sensors = useSensors(useSensor(PointerSensor, { activationConstraint: { distance: 4 } }), useSensor(KeyboardSensor, { coordinateGetter: sortableKeyboardCoordinates }));
  const onStepDragEnd = ({ active, over }: DragEndEvent) => {
    if (!over || active.id === over.id) return;
    setSteps((prev) => arrayMove(prev, prev.findIndex((s) => s.key === active.id), prev.findIndex((s) => s.key === over.id)));
  };

  // Alt+M / Alt+P anywhere in the form.
  useEffect(() => {
    const onKey = (e: globalThis.KeyboardEvent) => {
      if (!e.altKey || e.metaKey || e.ctrlKey) return;
      if (e.code === "KeyM") {
        e.preventDefault();
        addStep("machining");
      } else if (e.code === "KeyP") {
        e.preventDefault();
        addStep("post_process");
      }
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [addStep]);

  const engineSteps: JobStep[] = useMemo(() => steps.map(({ key: _key, ...s }) => s), [steps]);

  const preview = useMemo<{ result?: ScheduleResult; error?: string }>(() => {
    if (!settings || !machines || !committed || !dueDate) return {};
    // A line still waiting for its machine has nowhere to run yet, so the preview leaves it out.
    const placed = engineSteps.map((s) => (s.type === "machining" ? { ...s, machines: s.machines.filter((m) => m.machineId) } : s));
    try {
      return {
        result: computeSchedule({
          steps: placed,
          dueDate,
          settings: engineSettings(settings, machines),
          committedJobs: committed,
          today,
          status,
        }),
      };
    } catch (e) {
      return { error: errorMessage(e) };
    }
  }, [settings, machines, committed, dueDate, engineSteps, today, status]);

  const cantMakeIt = preview.result?.riskLevel === "impossible";

  const onKeyDown = (e: KeyboardEvent<HTMLFormElement>) => {
    if (e.key !== "Enter") return;
    const target = e.target as HTMLElement;
    if (target.tagName === "TEXTAREA" || target.tagName === "BUTTON") return;
    e.preventDefault();
    if (e.metaKey || e.ctrlKey) {
      formRef.current?.requestSubmit();
      return;
    }
    const focusables = Array.from(formRef.current?.querySelectorAll<HTMLElement>("input, select, textarea") ?? []).filter((el) => !el.hasAttribute("disabled"));
    const idx = focusables.indexOf(target);
    const next = focusables[idx + 1];
    if (next) next.focus();
    else formRef.current?.requestSubmit();
  };

  const submit = async (e: FormEvent) => {
    e.preventDefault();
    setError(null);
    const name = customerName.trim();
    const existing = customers?.find((c) => c.name.toLowerCase() === name.toLowerCase());
    const payload = {
      customerId: existing?._id,
      newCustomerName: existing ? undefined : name,
      reference: reference.trim(),
      part: part.trim() || undefined,
      quantity: quantity.trim() ? Number(quantity) : undefined,
      notes: notes.trim() || undefined,
      dueDate,
      status,
      steps: engineSteps.map((s) =>
        s.type === "machining" ? { type: "machining" as const, machines: s.machines.map((m) => ({ machineId: m.machineId as Id<"machines">, hours: m.hours })) } : s,
      ),
    };
    setSaving(true);
    try {
      const id = jobId ? (await updateJob({ jobId, ...payload }), jobId) : await createJob(payload);
      onSaved(id);
    } catch (err) {
      setError(errorMessage(err));
    } finally {
      setSaving(false);
    }
  };

  const quote = status === "quoted";
  const saveLabel = saving ? "Saving…" : jobId ? "Save changes" : quote ? "Save quote" : "Add to board";
  /** Working days the step's machining takes: from the preview, or the biggest share at full rate before there is one. */
  const shopDays = (lines: readonly MachineHours[], stepIndex: number) => {
    const seg = preview.result?.timeline.find((s) => s.stepIndex === stepIndex);
    if (seg?.kind === "machining") return seg.workingDays;
    if (!settings || !machines || settings.workingDays.length === 0) return 0;
    const days = lines.map((line) => {
      const weekly = machines.active.find((m) => m._id === line.machineId)?.weeklyHours ?? 0;
      return weekly > 0 ? Math.ceil(line.hours / (weekly / settings.workingDays.length)) : 0;
    });
    return Math.max(1, ...days);
  };

  return (
    <form ref={formRef} onSubmit={submit} onKeyDown={onKeyDown} className="flex min-h-0 flex-1 flex-col">
      <div className="flex min-h-0 flex-1 flex-col gap-7 overflow-y-auto px-7 pt-[22px] pb-6 lg:flex-row">
        <div className="flex min-w-0 flex-1 flex-col gap-[22px]">
          <div className="flex flex-col gap-4">
            <div className="flex flex-col gap-3 sm:flex-row">
              <Field label="Customer" htmlFor="customer" className="flex-1">
                <div className="relative">
                  <input
                    id="customer"
                    className="input pr-10 [&::-webkit-calendar-picker-indicator]:opacity-0"
                    list="customer-options"
                    value={customerName}
                    onChange={(e) => setCustomerName(e.target.value)}
                    placeholder="Type to search or add"
                    autoFocus
                    required
                  />
                  <span className="pointer-events-none absolute top-1/2 right-3.5 -translate-y-1/2 text-muted">
                    <ChevronDownIcon />
                  </span>
                </div>
                <datalist id="customer-options">
                  {customers?.map((c) => (
                    <option key={c._id} value={c.name} />
                  ))}
                </datalist>
              </Field>
              <Field label={quote ? "Quote number" : "Job number"} htmlFor="reference" className="sm:w-[150px]">
                <input id="reference" className="input input-data" value={reference} onChange={(e) => setReference(e.target.value)} placeholder="AC-2291" required />
              </Field>
            </div>
            <div className="flex flex-col gap-3 sm:flex-row">
              <Field label="Part" htmlFor="part" className="flex-1">
                <input id="part" className="input" value={part} onChange={(e) => setPart(e.target.value)} placeholder="e.g. Sensor housing" maxLength={80} />
              </Field>
              <Field label="Quantity" htmlFor="quantity" className="sm:w-[150px]">
                <input
                  id="quantity"
                  type="number"
                  className="input input-data"
                  min={1}
                  step={1}
                  value={quantity}
                  onChange={(e) => setQuantity(e.target.value)}
                  placeholder="120"
                />
              </Field>
            </div>
            <div className="flex flex-col gap-3 sm:flex-row">
              <Field label="Due date" htmlFor="dueDate" className="flex-1">
                <div className="relative">
                  <span className={`pointer-events-none absolute top-1/2 left-3.5 -translate-y-1/2 ${cantMakeIt ? "text-risk-text" : "text-brand-dark"}`}>
                    <CalendarIcon />
                  </span>
                  <input
                    id="dueDate"
                    type="date"
                    className={`input input-data pl-10 focus:pl-[39px] ${cantMakeIt ? "border-2 border-risk pl-[39px] focus:border-risk focus:ring-risk-tint" : ""}`}
                    value={dueDate}
                    min={jobId ? undefined : today}
                    onChange={(e) => setDueDate(e.target.value)}
                    aria-invalid={cantMakeIt}
                    aria-describedby={cantMakeIt ? "due-error" : undefined}
                    required
                  />
                </div>
                {cantMakeIt && (
                  <p id="due-error" className="mt-1.5 text-xs leading-4 font-semibold text-risk-text">
                    Can't be met with these steps
                  </p>
                )}
              </Field>
              <Field label="Stage" htmlFor="stage" className="sm:w-[216px]">
                {status === "quoted" || status === "scheduled" ? (
                  <div id="stage" className="segmented h-11" role="radiogroup" aria-label="Stage">
                    {(["quoted", "scheduled"] as const).map((s) => (
                      <button key={s} type="button" role="radio" aria-checked={status === s} className="segment" onClick={() => setStatus(s)}>
                        {STATUS_META[s].label}
                      </button>
                    ))}
                  </div>
                ) : (
                  <select id="stage" className="input" value={status} onChange={(e) => setStatus(e.target.value as JobStatus)}>
                    {(Object.keys(STATUS_META) as JobStatus[]).map((s) => (
                      <option key={s} value={s}>
                        {STATUS_META[s].label}
                      </option>
                    ))}
                  </select>
                )}
              </Field>
            </div>
            <Field label="Notes" htmlFor="notes" optional>
              <textarea id="notes" className="input" rows={2} value={notes} onChange={(e) => setNotes(e.target.value)} />
            </Field>
          </div>

          <section className="flex flex-col gap-2.5">
            <div className="flex items-baseline justify-between">
              <h3 className="text-[15px] leading-[18px] font-extrabold">Steps</h3>
              <span className="text-xs text-muted">First to last. Drag to reorder.</span>
            </div>
            {steps.length === 0 && <p className="rounded-md border border-dashed border-line p-4 text-center text-sm text-muted">No steps yet. Add machining hours or a post-process.</p>}
            <DndContext sensors={sensors} collisionDetection={closestCenter} onDragEnd={onStepDragEnd}>
              <SortableContext items={steps.map((s) => s.key)} strategy={verticalListSortingStrategy}>
                <ol className="flex flex-col gap-2.5">
                  {steps.map((step, i) => (
                    <StepEditor
                      key={step.key}
                      step={step}
                      index={i}
                      autoFocus={focusKey === step.key}
                      machines={machines}
                      shopDays={step.type === "machining" ? shopDays(step.machines, i) : 0}
                      onChange={(patch) => updateStep(step.key, patch)}
                      onRemove={() => removeStep(step.key)}
                    />
                  ))}
                </ol>
              </SortableContext>
            </DndContext>
            <datalist id="post-process-options">
              {POST_PROCESS_SUGGESTIONS.map((s) => (
                <option key={s} value={s} />
              ))}
            </datalist>
            <div className="flex flex-wrap gap-2 pt-1">
              <button type="button" className="btn-add" onClick={() => addStep("machining")}>
                + Machining
                <span className="kbd-group">
                  <kbd>Alt</kbd>
                  <kbd>M</kbd>
                </span>
              </button>
              <button type="button" className="btn-add" onClick={() => addStep("post_process")}>
                + Post-process
                <span className="kbd-group">
                  <kbd>Alt</kbd>
                  <kbd>P</kbd>
                </span>
              </button>
            </div>
          </section>
          {error && <p className="notice-error">{error}</p>}
        </div>

        <ResultPanel
          result={preview.result}
          error={preview.error}
          dueDate={dueDate}
          customerName={customerName.trim()}
          committed={committed}
          workingDays={settings?.workingDays}
          machines={machines}
        />
      </div>

      <footer className="flex flex-wrap items-center justify-between gap-3 border-t border-line px-7 py-3.5">
        <span className="flex items-center gap-2 text-xs text-muted">
          <span className="kbd-group">
            <kbd>{IS_MAC ? "⌘" : "Ctrl"}</kbd>
            <kbd>Enter</kbd>
          </span>
          to save
        </span>
        <div className="flex flex-wrap items-center gap-2.5">
          {cantMakeIt && dueDate && <span className="text-xs text-muted">Saving keeps {formatDateLong(dueDate)} and flags it Can't make it</span>}
          <button type="button" className="btn-secondary btn-md px-[18px] py-[11px] text-sm" onClick={onCancel}>
            Cancel
          </button>
          <button type="submit" className="btn-primary btn-md py-[11px] text-sm" disabled={saving}>
            {saveLabel}
          </button>
        </div>
      </footer>
    </form>
  );
}

function Field({ label, htmlFor, optional, className = "", children }: { label: string; htmlFor: string; optional?: boolean; className?: string; children: ReactNode }) {
  return (
    <div className={`flex flex-col ${className}`}>
      <label className="label" htmlFor={htmlFor}>
        {label} {optional && <span className="font-normal text-subtle">(optional)</span>}
      </label>
      {children}
    </div>
  );
}

/** Step rows from design system v2.1: grip, number, marker, name, then mini inputs; bin on the right. */
function StepEditor({
  step,
  index,
  autoFocus,
  machines,
  shopDays,
  onChange,
  onRemove,
}: {
  step: StepRow;
  index: number;
  autoFocus: boolean;
  machines?: ShopMachines;
  shopDays: number;
  onChange: (patch: Partial<JobStep>) => void;
  onRemove: () => void;
}) {
  const { attributes, listeners, setNodeRef, setActivatorNodeRef, transform, transition, isDragging } = useSortable({ id: step.key });
  const kind = step.type === "post_process" ? processKind(step.name) : null;

  return (
    <li
      ref={setNodeRef}
      style={{ transform: CSS.Translate.toString(transform), transition }}
      className={`flex items-start gap-2 rounded-md border border-line bg-bg px-3 py-2.5 sm:gap-3 ${isDragging ? "relative z-10 shadow-drag" : ""}`}
    >
      <button
        ref={setActivatorNodeRef}
        type="button"
        className="mt-2.5 cursor-grab text-quoted active:cursor-grabbing"
        aria-label={`Move step ${index + 1}`}
        {...attributes}
        {...listeners}
      >
        <GripIcon />
      </button>
      <span className="mt-[11px] w-3 shrink-0 font-mono text-[11px] leading-[14px] text-subtle">{index + 1}</span>
      <span className={`mt-3 size-3 shrink-0 rounded-[3px] ${step.type === "machining" ? "bg-ink" : PROCESS_META[kind!].dot}`} />
      {step.type === "machining" ? (
        <>
          <div className="mt-[3px] flex w-[104px] min-w-0 shrink flex-col gap-px">
            <span className="text-sm leading-[18px] font-bold">Machining</span>
            {stepHours(step) > 0 && <span className="font-mono text-[11px] leading-[14px] text-muted">= {plural(shopDays, "shop day")}</span>}
          </div>
          <MachineLines step={step} index={index} autoFocus={autoFocus} machines={machines} onChange={(lines) => onChange({ machines: lines })} />
        </>
      ) : (
        <>
          <div className="mt-[3px] flex w-[104px] min-w-0 shrink flex-col gap-px">
            <span className="text-sm leading-[18px] font-bold">{PROCESS_META[kind!].short ?? "Outside"}</span>
            <span className="font-mono text-[11px] leading-[14px] text-muted">post-process</span>
          </div>
          <div className="flex min-w-0 flex-1 flex-wrap gap-2">
            <input
              className="input h-9 w-44 rounded-[10px] px-2.5 text-[13px] focus:px-[9px]"
              list="post-process-options"
              placeholder="Pick or type one"
              value={step.name}
              autoFocus={autoFocus}
              aria-label={`Step ${index + 1} process`}
              onChange={(e) => onChange({ name: e.target.value })}
              required
            />
            <MiniNumber label="calendar days" className="w-32" value={step.leadDays} integer ariaLabel={`Step ${index + 1} days at the vendor`} onChange={(v) => onChange({ leadDays: v })} />
            <MiniNumber label="day courier out" className="w-[150px]" value={step.transitDaysOut} integer ariaLabel={`Step ${index + 1} working days courier out`} onChange={(v) => onChange({ transitDaysOut: v })} />
            <MiniNumber label="day courier back" className="w-[150px]" value={step.transitDaysBack} integer ariaLabel={`Step ${index + 1} working days courier back`} onChange={(v) => onChange({ transitDaysBack: v })} />
          </div>
        </>
      )}
      <button type="button" className="mt-2.5 text-subtle hover:text-risk-text" aria-label={`Remove step ${index + 1}`} onClick={onRemove} tabIndex={-1}>
        <TrashIcon />
      </button>
    </li>
  );
}

/**
 * A machining step's hours, one line per machine: "[80 h] on [M1]". The machines run at the same
 * time, so the step takes as long as its biggest line (D49).
 */
function MachineLines({
  step,
  index,
  autoFocus,
  machines,
  onChange,
}: {
  step: Extract<JobStep, { type: "machining" }>;
  index: number;
  autoFocus: boolean;
  machines?: ShopMachines;
  onChange: (lines: MachineHours[]) => void;
}) {
  const lines = step.machines;
  // "+ Another machine" moves focus to the new line's hours, which mounts with autoFocus.
  const [focusNewLine, setFocusNewLine] = useState(false);
  const used = new Set(lines.map((l) => l.machineId));
  const spare = machines?.active.find((m) => !used.has(m._id));
  const setLine = (j: number, patch: Partial<MachineHours>) => onChange(lines.map((l, k) => (k === j ? { ...l, ...patch } : l)));

  return (
    <div className="flex min-w-0 flex-1 flex-col gap-2">
      {lines.map((line, j) => {
        // Machines this line can switch to: active ones not on another line, plus its own even if archived.
        const options = machines?.active.filter((m) => m._id === line.machineId || !used.has(m._id)) ?? [];
        const ownMissing = line.machineId && !options.some((m) => m._id === line.machineId);
        return (
          <div key={j} className="flex flex-wrap items-center gap-2">
            <MiniNumber
              label="h"
              className="w-[84px]"
              value={line.hours}
              step={0.5}
              autoFocus={(autoFocus || focusNewLine) && j === lines.length - 1}
              ariaLabel={`Step ${index + 1} machining hours${lines.length > 1 ? ` on line ${j + 1}` : ""}`}
              onChange={(v) => setLine(j, { hours: v })}
            />
            <span className="text-xs text-muted">on</span>
            <select
              className="input h-9 w-40 rounded-[10px] px-2.5 text-[13px] focus:px-[9px]"
              value={line.machineId}
              aria-label={`Step ${index + 1} machine${lines.length > 1 ? ` on line ${j + 1}` : ""}`}
              onChange={(e) => setLine(j, { machineId: e.target.value })}
              required
            >
              <option value="" disabled>
                {machines ? "Pick a machine" : "Loading…"}
              </option>
              {ownMissing && <option value={line.machineId}>{machines?.name(line.machineId)}</option>}
              {options.map((m) => (
                <option key={m._id} value={m._id}>
                  {m.name}
                </option>
              ))}
            </select>
            {lines.length > 1 && (
              <button type="button" className="text-xs text-subtle hover:text-risk-text" onClick={() => onChange(lines.filter((_, k) => k !== j))} tabIndex={-1}>
                Remove
              </button>
            )}
          </div>
        );
      })}
      <div className="flex flex-wrap items-center gap-x-3 gap-y-1">
        {spare && (
          <button
            type="button"
            className="text-xs font-bold text-brand-dark hover:underline"
            onClick={() => {
              setFocusNewLine(true);
              onChange([...lines, { machineId: spare._id, hours: 0 }]);
            }}
          >
            + Another machine
          </button>
        )}
        {lines.length > 1 && <span className="font-mono text-[11px] leading-[14px] text-muted">{formatHours(stepHours(step))} in all, at the same time</span>}
      </div>
    </div>
  );
}

/** 36px mini input with its unit suffix in 12px muted text. */
function MiniNumber({
  label,
  value,
  onChange,
  className,
  step = 1,
  integer = false,
  autoFocus,
  ariaLabel,
}: {
  label: string;
  value: number;
  onChange: (v: number) => void;
  className: string;
  step?: number;
  integer?: boolean;
  autoFocus?: boolean;
  ariaLabel: string;
}) {
  return (
    <label className={`flex h-9 shrink-0 items-center gap-1.5 rounded-[10px] border border-line bg-bg px-2.5 focus-within:border-brand focus-within:ring-4 focus-within:ring-brand-tint ${className}`}>
      <input
        type="number"
        className="w-full min-w-0 bg-transparent font-mono text-sm leading-[18px] outline-none"
        min={0}
        step={step}
        value={integer ? value : value || ""}
        placeholder={integer ? undefined : "0"}
        autoFocus={autoFocus}
        aria-label={ariaLabel}
        required={!integer}
        onChange={(e) => onChange(integer ? Math.max(0, Math.floor(Number(e.target.value) || 0)) : Number(e.target.value))}
      />
      <span className="text-xs leading-4 whitespace-nowrap text-muted">{label}</span>
    </label>
  );
}

/** The live result panel: latest start tile, schedule strip, step dates and the capacity callout. */
function ResultPanel({
  result,
  error,
  dueDate,
  customerName,
  committed,
  workingDays,
  machines,
}: {
  result?: ScheduleResult;
  error?: string;
  dueDate: string;
  customerName: string;
  committed?: CommittedJob[];
  workingDays?: readonly number[];
  machines?: ShopMachines;
}) {
  return (
    <aside className="flex h-fit w-full shrink-0 flex-col gap-[18px] rounded-lg bg-surface p-6 lg:w-[476px]">
      <div className="flex items-center justify-between">
        <span className="font-mono text-[11px] leading-[14px] font-bold tracking-[0.05em] text-muted uppercase">Worked out as you type</span>
        {result && <RiskBadge risk={result.riskLevel} className="px-2.5 py-1 text-xs leading-4" />}
      </div>
      {!dueDate && <p className="text-sm text-muted">Enter a due date to see the latest start.</p>}
      {error && <p className="notice-error">{error}</p>}
      {result && workingDays && machines && (
        <ResultDetails result={result} dueDate={dueDate} customerName={customerName} committed={committed ?? []} workingDays={workingDays} machines={machines} />
      )}
    </aside>
  );
}

function ResultDetails({
  result,
  dueDate,
  customerName,
  committed,
  workingDays,
  machines,
}: {
  result: ScheduleResult;
  dueDate: string;
  customerName: string;
  committed: CommittedJob[];
  workingDays: readonly number[];
  machines: ShopMachines;
}) {
  const late = result.riskLevel === "impossible";
  const items = planItems(result, workingDays);
  const busiest = busiestMachineWeek(result, committed, machines);
  const [line1, line2] = workingDaysFromToday(result.slackWorkingDays).split(/ (?=from|ago)/);

  return (
    <>
      <div className={`flex items-end justify-between gap-4 rounded-tile px-[22px] py-5 text-white ${late ? "bg-late" : "bg-brand"}`}>
        <div className="flex flex-col gap-1.5">
          <span className={`tile-label ${late ? "text-white/80" : "text-brand-tint"}`}>Latest start</span>
          <span className="heading text-[40px] leading-none tracking-[-0.03em]">{formatDateLong(result.latestStartDate)}</span>
        </div>
        <span className="text-right text-[13px] leading-[1.4] font-semibold">
          {line1}
          {line2 && (
            <>
              <br />
              {line2}
            </>
          )}
        </span>
      </div>
      <PlanStrip result={result} workingDays={workingDays} />
      <dl className="flex flex-col">
        {items.map((item) => (
          <div key={`${item.stepIndex}-${item.kind}`} className="flex justify-between gap-3 border-t border-line py-2">
            <dt className="text-[13px] leading-4 text-muted">{planItemTitle(item)}</dt>
            <dd className="text-right font-mono text-xs leading-4">{formatRange(item.start, item.end)}</dd>
          </div>
        ))}
        <div className="flex justify-between gap-3 border-t border-line py-2">
          <dt className="text-[13px] leading-4 text-muted">{customerName ? `Due at ${customerName}` : "Due"}</dt>
          <dd className="text-right font-mono text-xs leading-4">{formatDateLong(dueDate)}</dd>
        </div>
      </dl>
      {late && !result.feasible ? (
        <Callout tone="late" title="A machine on this job has no hours">
          Give it weekly hours in Shop week, or put these hours on another machine.
        </Callout>
      ) : late ? (
        <Callout tone="late" title={`The latest start was ${formatDateLong(result.latestStartDate)}`}>
          These steps need {plural(-result.slackWorkingDays, "more working day")} than there are before the due date. Move the date or shorten the steps.
        </Callout>
      ) : (
        busiest && (
          <Callout tone="tight" title={`${busiest.name} is at ${Math.round(busiest.hours)} of ${formatHours(busiest.capacity)} in ${weekCommencing(busiest.weekStart)}`}>
            It fits, but there is little room left on that machine that week.
          </Callout>
        )
      )}
    </>
  );
}

function Callout({ tone, title, children }: { tone: "tight" | "late"; title: string; children: ReactNode }) {
  const style = tone === "tight" ? "bg-tight-tint text-tight-text" : "bg-late-tint text-late";
  return (
    <div className={`flex items-start gap-2.5 rounded-md px-3.5 py-3 ${style}`} role="status">
      <WarningIcon />
      <div className="flex flex-col gap-0.5">
        <span className="text-[13px] leading-4 font-extrabold">{title}</span>
        <span className="text-xs leading-4">{children}</span>
      </div>
    </div>
  );
}

/**
 * The fullest machine-week this job's machining lands in, counting the job itself, when it
 * reaches 90% of that machine's weekly hours and other jobs share that week (D49). A job that
 * fills a machine on its own is no squeeze on anything else, so it isn't flagged.
 */
function busiestMachineWeek(result: ScheduleResult, committed: CommittedJob[], machines: ShopMachines) {
  const windows = machiningWindows(result);
  const allocations = windows.flatMap((w) => w.allocations);
  const withJob = [...committed, { id: "this-job", machining: windows }];
  let best: { name: string; weekStart: string; hours: number; capacity: number; share: number } | null = null;
  for (const machine of machines.active) {
    const days = allocations.filter((a) => a.machineId === machine._id).map((a) => a.date);
    if (days.length === 0 || machine.weeklyHours <= 0) continue;
    const first = startOfWeek(days.reduce((a, b) => (a < b ? a : b)));
    const last = startOfWeek(days.reduce((a, b) => (a > b ? a : b)));
    const weeks = Math.round((Date.parse(last) - Date.parse(first)) / (7 * 86_400_000)) + 1;
    const others = weeklyLoad(committed, first, weeks, machine._id);
    for (const [k, w] of weeklyLoad(withJob, first, weeks, machine._id).entries()) {
      if (!days.some((d) => startOfWeek(d) === w.weekStart) || others[k]!.hours <= 0) continue;
      const share = w.hours / machine.weeklyHours;
      if (share >= 0.9 && (!best || share > best.share)) best = { name: machine.name, weekStart: w.weekStart, hours: w.hours, capacity: machine.weeklyHours, share };
    }
  }
  return best;
}
